/**
 * rooms.js — SDK client per le ROOM (stanze di gruppo effimere).
 *
 * Fa da ponte tra le Cloud Functions room (functions/src/callable/rooms.ts) e la
 * UI, usando roomCrypto per la derivazione dell'identità usa-e-getta e il
 * fan-out pairwise. Le chiavi private restano SOLO in memoria sul device.
 *
 * NB: lato server le room sono dietro feature-flag (ROOMS_DISABLED finché non
 * c'è l'ok legale; bypass founder per test).
 */

import { callFn } from './firebase';
import { mnemonicToSeed } from './mnemonic';
import { deriveRoomIdentity, encryptForRoom, decryptRoomMessage } from './roomCrypto';

function genRoomId() {
  const b = crypto.getRandomValues(new Uint8Array(18)); // 24 char base64url
  let s = '';
  b.forEach((x) => { s += String.fromCharCode(x); });
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function unwrap(res, what) {
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || `${what} fallito`);
  return d;
}

/** Identità usa-e-getta del chiamante per una room (deterministica). */
export async function roomIdentity(mnemonic, roomId) {
  if (!mnemonic) throw new Error('mnemonic non in memoria');
  const seed = mnemonicToSeed(mnemonic);
  return deriveRoomIdentity(seed, roomId);
}

/** Crea una room. type: 'event'|'work'. Ritorna { roomId, identity }. */
export async function createRoom({ mnemonic, type, memberCap, ttlSeconds, nickname }) {
  const roomId = genRoomId();
  const id = await roomIdentity(mnemonic, roomId);
  unwrap(await callFn('createRoom')({
    roomId, type, memberCap, ttlSeconds,
    creatorHandle: id.handle, creatorPubKeyBox: id.pubKeyBox, nickname,
  }), 'createRoom');
  return { roomId, identity: id };
}

/** Genera un invito per la room (solo il creatore). Ritorna { token, passcode? }. */
export async function inviteToRoom({ roomId, requirePasscode, multiUseMax, ttlSeconds }) {
  const d = unwrap(await callFn('generateRoomInvite')({ roomId, requirePasscode, multiUseMax, ttlSeconds }), 'generateRoomInvite');
  return { token: d.token, passcode: d.passcode || null };
}

/** Sbircia un invito (senza consumarlo): { roomId, type, requirePasscode }. */
export async function peekRoomInvite(token) {
  return unwrap(await callFn('peekRoomInvite')({ token }), 'peekRoomInvite');
}

/** Entra in una room via invito. Ritorna { roomId, type, identity }. */
export async function joinRoom({ mnemonic, token, passcode, nickname }) {
  const peek = await peekRoomInvite(token);
  const id = await roomIdentity(mnemonic, peek.roomId);
  unwrap(await callFn('redeemRoomInvite')({
    token, passcode, joinerHandle: id.handle, joinerPubKeyBox: id.pubKeyBox, nickname,
  }), 'redeemRoomInvite');
  return { roomId: peek.roomId, type: peek.type, identity: id };
}

/** Membri correnti della room (per il fan-out). Verifica membership lato server. */
export async function getMembers(roomId, identity) {
  const d = unwrap(await callFn('getRoomMembers')({ roomId, myHandle: identity.handle }), 'getRoomMembers');
  return {
    type: d.type,
    creatorHandle: d.creatorHandle,
    memberCap: d.memberCap,
    ttlMaxAt: d.ttlMaxAt ?? null,
    members: d.members || [],
    iAmCreator: d.creatorHandle === identity.handle,
  };
}

/**
 * Invia un messaggio alla room (fan-out): cifra per OGNI membro (me incluso →
 * self-read). `members` da getMembers() include già il mio handle.
 */
export async function sendToRoom({ roomId, identity, members, text, ttl = 86400 }) {
  const payloads = await encryptForRoom(text, identity.secKeyBox, members);
  const d = unwrap(await callFn('sendRoomMessage')({
    roomId, senderHandle: identity.handle, senderPubKeyBox: identity.pubKeyBox,
    senderTTL: ttl, encryptedPayloads: payloads,
  }), 'sendRoomMessage');
  return { msgId: d.msgId };
}

/**
 * Scarica e DECIFRA i messaggi della room. Ogni messaggio è decifrato col
 * payload destinato al mio handle (null se inviato prima che entrassi).
 */
export async function fetchMessages({ roomId, identity, limit }) {
  const d = unwrap(await callFn('listRoomMessages')({ roomId, myHandle: identity.handle, limit }), 'listRoomMessages');
  const out = [];
  for (const m of d.messages || []) {
    let text = null;
    try {
      text = await decryptRoomMessage(m.encryptedPayloads, identity.handle, m.senderPubKeyBox, identity.secKeyBox);
    } catch { text = null; } // payload non per me o corrotto → salta
    out.push({
      id: m.id, senderHandle: m.senderHandle, createdAt: m.createdAt,
      mine: m.senderHandle === identity.handle, text,
    });
  }
  return out;
}

/** Brucia la room (solo il creatore): cancella tutto per tutti, subito. */
export async function burnRoom(roomId) {
  unwrap(await callFn('burnRoom')({ roomId }), 'burnRoom');
  return true;
}

/** Le room che HO CREATO (per la gestione + cap 3). */
export async function listMyRooms() {
  const d = unwrap(await callFn('listMyRooms')({}), 'listMyRooms');
  return d.rooms || [];
}
